import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, TextField, Autocomplete } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { dispatch, useSelector } from '../../../../redux/store';
import { addGuestPayment } from '../../../../redux/slices/guestRedux/guestBillingRedux';
import { getGuestFromBard } from '../../../../redux/slices/guestRedux/guestRedux';
import { PAYMENT_OPTIONS } from '../../../../utils/constants';

export default function BillingPayments({ cardId, selectedData }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    paymentType: '',
    amount: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalAmountBeforeTax = selectedData?.guests?.billing?.reduce((sum, item) => sum + item.amount, 0) || 0;
  const totalGovTax = totalAmountBeforeTax * 0.025;
  const totalAmount = totalAmountBeforeTax+totalGovTax;
  const paidAmount = selectedData?.guests?.payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
  const balanceAmount = totalAmount - paidAmount;

  let guestBillingAdded;
  ({ addData: guestBillingAdded } = useSelector((state) => state.GuestBillings));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePaymentTypeChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      paymentType: newValue || '',
    }));
  };

  useEffect(() => {
    if (guestBillingAdded?.data) {
      dispatch(getGuestFromBard(cardId));
    }
  }, [guestBillingAdded, cardId]);

  const validateForm = () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return false;
    }
    if (formData.amount < 0) {
      toast.error('Amount cannot be negative');
      return false;
    }
    if (formData.amount > (balanceAmount+1)) {
      toast.error('Amount should be less than ' + balanceAmount);
      return false;
    }
    if (!formData.paymentType) {
      toast.error('Payment Type is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await dispatch(
        addGuestPayment({
          name: formData.title,
          description: formData.description,
          paymentType: formData.paymentType,
          price: formData.amount,
          cardId: cardId,
        })
      );

      toast.success('Payment added successfully!');
      setFormData({
        title: '',
        description: '',
        paymentType: '',
        amount: 0,
      });
    } catch (error) {
      toast.error('Failed to add payment. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Mark Payment
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={3}
          />
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            value={formData.paymentType}
            onChange={handlePaymentTypeChange}
            options={PAYMENT_OPTIONS}
            renderInput={(params) => <TextField label="Payment Type" name="paymentType" {...params} />}
            clearOnBlur
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Amount"
            name="amount"
            type="number"
            value={formData.amount}
            onChange={handleChange}
            inputProps={{ min: 0, step: "any" }}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <LoadingButton
            type="submit"
            variant="contained"
            fullWidth
            loading={isSubmitting}
          >
            {isSubmitting? 'Adding...': 'Mark Payment'}
          </LoadingButton>
        </Grid>
      </Grid>
    </Box>
  );
}